import type { CommandOf, CommandResult } from '@/core/app/commands';
import type { NpcId, ProductId } from '@/core/content/ids';
import type { ContentRegistry } from '@/core/content/registry';
import type { OrderTemplateDef } from '@/core/content/schemas/progressionDefs';
import type { DomainEvent } from '@/core/events/types';
import type { GameSave, SlotContent } from '@/core/persistence/schema/v1';
import { evaluateAll } from './conditions';
import { addFriendship } from './friendship';
import type { SlotKey } from './placement/geometry';
import type { HandlerEnv } from './placement/handlers';
import { createRng, type Rng } from './rng';
import { isFavorite } from './shop';

/** NPC orders (content-schemas §14.4): gentle requests, no timers, no penalty for "để sau". */
const ORDERS_UNLOCK = 'unl_npc_orders';
const FIRST_ORDER_NPC: NpcId = 'npc_bao';
const NPC_ORDER: readonly NpcId[] = ['npc_lan', 'npc_bao', 'npc_mit'];

type Line = { itemId: ProductId; qty: number };

const ordersOpen = (s: GameSave) => s.unlockedIds.includes(ORDERS_UNLOCK);

function productUnlocked(content: ContentRegistry, s: GameSave, id: ProductId): boolean {
  const def = content.items.get(id);
  return def?.kind === 'product' && (!def.unlockId || s.unlockedIds.includes(def.unlockId));
}

function buildLines(content: ContentRegistry, s: GameSave, tpl: OrderTemplateDef, rng: Rng): Line[] {
  const products = [...content.items.values()]
    .filter((d) => d.kind === 'product')
    .map((d) => d.id as ProductId)
    .filter((id) => productUnlocked(content, s, id));
  const lines: Line[] = [];
  for (const pick of tpl.picks) {
    let cand = products.filter((id) => {
      const def = content.items.get(id);
      const byId = pick.from.itemIds?.includes(id) ?? false;
      const byCat =
        def?.productCategory !== undefined && (pick.from.categories?.includes(def.productCategory) ?? false);
      return (byId || byCat) && !lines.some((l) => l.itemId === id);
    });
    if (pick.from.favoritesOnly) cand = cand.filter((id) => isFavorite(content, tpl.npcId, id));
    if (cand.length === 0) continue;
    const itemId = rng.weighted(cand, (id) =>
      isFavorite(content, tpl.npcId, id) ? content.balance.orderFavoriteWeight : 1,
    );
    lines.push({ itemId, qty: rng.int(pick.qty.min, pick.qty.max) });
  }
  // Clamp the total into [min, max] (every line keeps at least 1).
  const total = () => lines.reduce((n, l) => n + l.qty, 0);
  for (let guard = 0; total() > tpl.totalQty.max && guard < 50; guard++) {
    const biggest = lines.reduce((a, b) => (b.qty > a.qty ? b : a));
    if (biggest.qty <= 1) break;
    biggest.qty -= 1;
  }
  const first = lines[0];
  for (let guard = 0; first && total() < tpl.totalQty.min && guard < 50; guard++) first.qty += 1;
  return lines;
}

const round5 = (n: number, step: number) => Math.max(step, Math.round(n / step) * step);

/** Create one order if a met neighbour has none open. Deterministic: uses orders.rngState. */
export function createOrder(draft: GameSave, content: ContentRegistry, preferNpc?: NpcId): DomainEvent[] {
  const O = draft.orders;
  if (O.open.length >= content.balance.maxOpenOrders) return [];
  const cands = [...content.npcs.values()]
    .filter((n) => n.kind === 'human' && n.canShop && draft.flags[n.metFlag] === true)
    .filter((n) => !O.open.some((o) => o.npcId === n.id))
    .map((n) => n.id);
  if (cands.length === 0) return [];
  const rng = createRng(O.rngState);
  const fresh = NPC_ORDER.filter((id) => cands.includes(id) && !O.firstOrderDone[id]);
  const npcId: NpcId = preferNpc && cands.includes(preferNpc) ? preferNpc : (fresh[0] ?? rng.pick(cands));
  const npc = content.npcs.get(npcId);
  if (!npc) return [];
  let tpl: OrderTemplateDef | undefined;
  if (!O.firstOrderDone[npcId] && npc.firstOrderTemplateId) {
    tpl = content.orderTemplates.get(npc.firstOrderTemplateId);
  } else {
    const pool = npc.orderTemplates
      .map((id) => content.orderTemplates.get(id))
      .filter(
        (t): t is OrderTemplateDef => !!t && !t.isFirst && evaluateAll(content, draft, t.conditions).ok,
      );
    if (pool.length > 0) tpl = rng.weighted(pool, (t) => t.weight);
  }
  if (!tpl) {
    O.rngState = rng.state;
    return [];
  }
  const lines = buildLines(content, draft, tpl, rng);
  O.rngState = rng.state;
  if (lines.length === 0) return [];
  const mult = tpl.rewardMultiplier ?? content.balance.orderRewardMultiplier;
  const value = lines.reduce((n, l) => n + (content.items.get(l.itemId)?.sellPrice ?? 0) * l.qty, 0);
  const rewardCoins = round5(value * mult, content.balance.orderRoundTo);
  O.orderSeq += 1;
  const orderId = `o_${String(O.orderSeq)}` as const;
  O.open.push({
    orderId,
    templateId: tpl.id,
    npcId,
    lines,
    rewardCoins,
    friendship: tpl.friendship ?? content.balance.friendshipPoints.order,
    createdAtPlayMs: draft.clock.playTimeMs,
  });
  return [{ type: 'ORDER_CREATED', orderId, npcId, templateId: tpl.id, lines, rewardCoins }];
}

function scheduleNext(draft: GameSave, content: ContentRegistry): void {
  const O = draft.orders;
  O.nextOrderAtPlayMs =
    O.open.length < content.balance.maxOpenOrders
      ? draft.clock.playTimeMs + content.balance.orderIntervalSec * 1000
      : null;
}

/** Called from advanceTime: a new order every orderIntervalSec of play while there is room. */
export function orderTick(draft: GameSave, env: HandlerEnv): DomainEvent[] {
  if (!ordersOpen(draft)) return [];
  const O = draft.orders;
  const t = draft.clock.playTimeMs;
  if (O.open.length >= env.content.balance.maxOpenOrders) {
    O.nextOrderAtPlayMs = null;
    return [];
  }
  if (O.nextOrderAtPlayMs === null) {
    O.nextOrderAtPlayMs = t + env.content.balance.orderIntervalSec * 1000;
    return [];
  }
  if (t < O.nextOrderAtPlayMs) return [];
  const events = createOrder(draft, env.content);
  scheduleNext(draft, env.content);
  return events;
}

/** The first order arrives right when orders unlock (Anh Bảo's "3 gói mì", plan §1). */
export function ordersSystem(
  draft: GameSave,
  events: readonly DomainEvent[],
  env: HandlerEnv,
): DomainEvent[] {
  if (!events.some((e) => e.type === 'UNLOCK_GRANTED' && e.unlockId === ORDERS_UNLOCK)) return [];
  const out = createOrder(draft, env.content, FIRST_ORDER_NPC);
  scheduleNext(draft, env.content);
  return out;
}

/** How many units the player can hand over right now: the bag plus Tiệm shelves. */
export function availableUnits(s: GameSave, itemId: ProductId): number {
  const shelved = Object.values(s.rooms.minimart.slots).filter(
    (c) => c.kind === 'product' && c.itemId === itemId,
  ).length;
  return (s.inventory.productStacks[itemId] ?? 0) + shelved;
}

export function fulfillOrder(
  draft: GameSave,
  cmd: CommandOf<'fulfillOrder'>,
  env: HandlerEnv,
): CommandResult {
  const O = draft.orders;
  const order = O.open.find((o) => o.orderId === cmd.orderId);
  if (!order) return { ok: false, error: 'NOT_FOUND' };
  const missing = order.lines
    .map((l) => ({ itemId: l.itemId, missing: l.qty - availableUnits(draft, l.itemId) }))
    .filter((m) => m.missing > 0);
  if (missing.length > 0) return { ok: false, error: 'INSUFFICIENT_STOCK', detail: { missing } };

  const events: DomainEvent[] = [];
  let fromBagTotal = 0;
  let fromShelves = 0;
  for (const l of order.lines) {
    const have = draft.inventory.productStacks[l.itemId] ?? 0;
    const fromBag = Math.min(have, l.qty);
    draft.inventory.productStacks[l.itemId] = have - fromBag;
    fromBagTotal += fromBag;
    let rest = l.qty - fromBag;
    const slots = (Object.entries(draft.rooms.minimart.slots) as [SlotKey, SlotContent][])
      .filter(([, c]) => c.kind === 'product' && c.itemId === l.itemId)
      .map(([key]) => key)
      .sort();
    for (const key of slots) {
      if (rest <= 0) break;
      const shop = draft.rooms.minimart;
      shop.slots = Object.fromEntries(Object.entries(shop.slots).filter(([k]) => k !== key));
      rest -= 1;
      fromShelves += 1;
      events.push({
        type: 'PRODUCT_UNDISPLAYED',
        roomId: 'minimart',
        itemId: l.itemId,
        slotKey: key,
        to: 'order',
        origin: env.origin,
      });
    }
  }
  draft.wallet.coins += order.rewardCoins;
  env.ledger?.push({
    txId: cmd.txId,
    saveId: draft.saveId,
    atWallMs: env.nowWallMs,
    atPlayMs: draft.clock.playTimeMs,
    kind: 'order',
    coinsDelta: order.rewardCoins,
    balanceAfter: draft.wallet.coins,
    refs: { orderId: order.orderId, npcId: order.npcId },
  });
  O.firstOrderDone[order.npcId] = true;
  O.open = O.open.filter((o) => o !== order);
  events.push(
    {
      type: 'COINS_CHANGED',
      delta: order.rewardCoins,
      balance: draft.wallet.coins,
      reason: 'order',
      ref: order.orderId,
    },
    {
      type: 'ORDER_FULFILLED',
      orderId: order.orderId,
      templateId: order.templateId,
      txId: cmd.txId,
      npcId: order.npcId,
      lines: order.lines,
      takenFrom: { bag: fromBagTotal, shelves: fromShelves },
      rewardCoins: order.rewardCoins,
      friendship: order.friendship,
    },
    ...addFriendship(draft, env.content, order.npcId, 'order', env.nowWallMs, order.friendship),
  );
  if (O.nextOrderAtPlayMs === null) scheduleNext(draft, env.content);
  return { ok: true, events };
}

/** "Để sau": the order goes away, no penalty. A scripted first order will come back later. */
export function skipOrder(draft: GameSave, cmd: CommandOf<'skipOrder'>, env: HandlerEnv): CommandResult {
  const O = draft.orders;
  const order = O.open.find((o) => o.orderId === cmd.orderId);
  if (!order) return { ok: false, error: 'NOT_FOUND' };
  O.open = O.open.filter((o) => o !== order);
  if (O.nextOrderAtPlayMs === null) scheduleNext(draft, env.content);
  return { ok: true, events: [{ type: 'ORDER_SKIPPED', orderId: order.orderId, npcId: order.npcId }] };
}
